/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import { useState, useRef, useEffect, useCallback } from 'react';
import type { TaxonomyCodeDto } from '@shared/types';
import { useAssignmentStore } from '../../stores/assignmentStore';

interface TaxonomySearchProps {
  onSelect: (taxonomy: TaxonomyCodeDto) => void;
  placeholder?: string;
}

export function TaxonomySearch({ onSelect, placeholder = 'Search taxonomy codes...' }: TaxonomySearchProps) {
  const { searchResults, isSearching, searchTaxonomy: doSearch } = useAssignmentStore();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const containerRef = useRef<HTMLDivElement>(null);

  const handleInput = useCallback((value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      doSearch(value);
      setIsOpen(value.length >= 1);
    }, 200);
  }, [doSearch]);

  const handleSelect = useCallback((tax: TaxonomyCodeDto) => {
    onSelect(tax);
    setQuery(tax.code + ' — ' + tax.displayName);
    setIsOpen(false);
  }, [onSelect]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => handleInput(e.target.value)}
        onFocus={() => { if (query.length >= 1) setIsOpen(true); }}
        placeholder={placeholder}
        aria-label={placeholder}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-dds-blue-500 focus:border-transparent"
      />
      {isSearching && (
        <div className="absolute right-3 top-2.5 text-gray-400 text-xs">Searching...</div>
      )}

      {isOpen && searchResults.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-auto">
          {searchResults.map((tax) => (
            <li
              key={tax.setId}
              onClick={() => handleSelect(tax)}
              className="px-3 py-2.5 hover:bg-dds-blue-50 cursor-pointer text-sm border-b border-gray-50 last:border-b-0"
            >
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium text-dds-blue-500">{tax.code}</span>
                  <span className="text-gray-400 mx-1">—</span>
                  <span className="text-gray-700">{tax.displayName}</span>
                </div>
              </div>
              {tax.grouping && (
                <div className="text-[11px] text-gray-400 mt-0.5">
                  {tax.section ? `${tax.section} · ` : ''}{tax.grouping}{tax.classification ? ` › ${tax.classification}` : ''}{tax.specialization ? ` › ${tax.specialization}` : ''}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {isOpen && searchResults.length === 0 && query.length >= 1 && !isSearching && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm text-gray-400">
          No matching taxonomy codes
        </div>
      )}
    </div>
  );
}
